import { Filters } from '@components/utility/filters';
import { Dropdown, Option, Select } from '@components/utility/selects';
import { useInboxContext } from '@inbox/contexts/InboxContext';
import {
    handleActivateError,
    handleActivateSuccess,
    handleDeactivateError,
    handleDeactivateSuccess,
    handleDeleteError,
    handleDeleteSuccess,
} from '@inbox/utils';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { useGetCountriesQuery } from '@services/countries';
import { renderCountryOption } from '@utils/common';
import type { ChangeEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

const STATUS_OPTIONS: Option[] = [
    { label: 'Active', value: 'true' },
    { label: 'Inactive', value: 'false' },
];

export enum DropdownFilters {
    IS_ACTIVE = 'is_active',
    COUNTRY = 'country',
}

export type InboxDropdownFilterValues = {
    [DropdownFilters.COUNTRY]: string[];
    [DropdownFilters.IS_ACTIVE]: string;
};

interface IInboxFiltersProps {
    activeFilters: InboxDropdownFilterValues;
    searchInput: string;
    onSelectClick: (e: SelectChangeEvent<string | string[]>) => void;
    onParamChange: (params: { field: string; value: string | string[] | null; isDelete?: boolean }) => void;
    onFilterReset: () => void;
    onSearchChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
    onSearchClear: () => void;
}

export const InboxFilters = ({
    searchInput,
    activeFilters: dropdownFilterValues,
    onSelectClick: handleSelectClick,
    onParamChange: handleParamChange,
    onFilterReset: handleFilterReset,
    onSearchChange: handleSearchChange,
    onSearchClear: handleSearchClear,
}: IInboxFiltersProps) => {
    const [dropdownAnchorEl, setDropdownAnchorEl] = useState<null | HTMLElement>(null);

    const { data: countries } = useGetCountriesQuery();
    const { selectedItems, removeSelectedItems, onBulkToggleActiveStatus, onBulkToggleInactiveStatus, onBulkDelete } =
        useInboxContext();

    const activeFilters = useMemo(
        () => ({
            ...(dropdownFilterValues[DropdownFilters.COUNTRY].length
                ? { [DropdownFilters.COUNTRY]: dropdownFilterValues[DropdownFilters.COUNTRY] }
                : {}),
            ...(dropdownFilterValues[DropdownFilters.IS_ACTIVE]
                ? { [DropdownFilters.IS_ACTIVE]: [dropdownFilterValues[DropdownFilters.IS_ACTIVE]] }
                : {}),
        }),
        [dropdownFilterValues]
    );

    const getChipLabel = useCallback(
        (field: string, value: string) => {
            switch (field) {
                case DropdownFilters.COUNTRY:
                    return value;
                case DropdownFilters.IS_ACTIVE:
                    return value === 'true' ? 'Active' : 'Inactive';
                default:
                    return value;
            }
        },
        [countries]
    );

    const handleBulkToggleActiveStatus = async (inboxIds: number[]) => {
        setDropdownAnchorEl(null);
        try {
            await onBulkToggleActiveStatus(inboxIds);
            handleActivateSuccess(inboxIds);
        } catch {
            handleActivateError(inboxIds);
        }
    };

    const handleBulkToggleInactiveStatus = async (inboxIds: number[]) => {
        setDropdownAnchorEl(null);
        try {
            await onBulkToggleInactiveStatus(inboxIds);
            handleDeactivateSuccess(inboxIds);
        } catch {
            handleDeactivateError(inboxIds);
        }
    };

    const handleBulkDelete = async (inboxIds: number[]) => {
        setDropdownAnchorEl(null);
        try {
            await onBulkDelete(inboxIds);
            removeSelectedItems(inboxIds);
            handleDeleteSuccess(inboxIds);
        } catch {
            handleDeleteError(inboxIds);
        }
    };

    const bulkActionsOptions = [
        {
            label: `Change Status to Active (${selectedItems.length})`,
            onClick: () => handleBulkToggleActiveStatus(selectedItems),
            value: 'change_status_active',
        },
        {
            label: `Change Status to Inactive (${selectedItems.length})`,
            onClick: () => handleBulkToggleInactiveStatus(selectedItems),
            value: 'change_status_inactive',
        },
        {
            label: `Remove (${selectedItems.length})`,
            onClick: () => handleBulkDelete(selectedItems),
            value: 'remove',
        },
    ];

    const bulkActionsDropdown = (
        <Dropdown
            anchorEl={dropdownAnchorEl}
            open={Boolean(dropdownAnchorEl)}
            onClose={() => setDropdownAnchorEl(null)}
            classes={{
                paper: 'min-w-[255px]',
            }}
            buttonProps={{
                fullWidth: true,
                className: 'h-full',
                onClick: (e) => setDropdownAnchorEl(e.currentTarget),
                startIcon: <SettingsOutlinedIcon />,
                children: <Typography fontFamily="Inter">Bulk Actions</Typography>,
                disabled: selectedItems.length === 0,
            }}
        >
            {bulkActionsOptions.map((option) => (
                <MenuItem key={option.value} onClick={option.onClick}>
                    {option.label}
                </MenuItem>
            ))}
        </Dropdown>
    );

    return (
        <Filters
            isOpen
            activeFilters={activeFilters}
            activeFiltersPlacement="bottom"
            withFilterIcon={false}
            dividers={false}
            onChipClick={(field, value) => handleParamChange({ field, value, isDelete: true })}
            onClearFilterClick={handleFilterReset}
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            onSearchClear={handleSearchClear}
            searchPlaceholder="Search by email"
            right={bulkActionsDropdown}
            getChipLabel={getChipLabel}
            className="!p-0"
        >
            <div className="my-3 grid grid-cols-4 gap-x-2.5 gap-y-5">
                <Select
                    searchable
                    multiple
                    name={DropdownFilters.COUNTRY}
                    size="small"
                    label="Country"
                    items={countries ?? []}
                    fcSX={{ size: 'small' }}
                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                    value={dropdownFilterValues[DropdownFilters.COUNTRY] ?? []}
                    renderOption={renderCountryOption}
                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string | string[]>)}
                    virtualize
                    className="rounded-lg"
                />
                <Select
                    name={DropdownFilters.IS_ACTIVE}
                    size="small"
                    label="Status"
                    items={STATUS_OPTIONS}
                    fcSX={{ size: 'small' }}
                    value={dropdownFilterValues[DropdownFilters.IS_ACTIVE]}
                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string | string[]>)}
                    className="rounded-lg"
                    type="radio"
                />
            </div>
        </Filters>
    );
};
